import React, { useRef, useState, useEffect } from "react";
import "./TestimonialsSection.css";
import { useTranslation } from "react-i18next";

const DEFAULT_TESTIMONIALS = [
  {
    rating: 5,
    text:
      "“nxNearby completely changed the way I learn. Instead of jumping randomly between YouTube, courses and blogs, the platform built a structured learning path for me. It actually understood my level and guided me step by step.”",
    name: "Sarah",
    role: "Software Engineer",
    avatar: "/Image/Hero/testimonials-profile.jpg",
  },
  {
    rating: 5,
    text:
      "“nxNearby completely changed the way I learn. Instead of jumping randomly between YouTube, courses and blogs, the platform built a structured learning path for me. It actually understood my level and guided me step by step.”",
    name: "Alex",
    role: "Product Designer",
    avatar: "/Image/Hero/testimonials-profile.jpg",
  },
  {
    rating: 5,
    text:
      "“nxNearby completely changed the way I learn. Instead of jumping randomly between YouTube, courses and blogs, the platform built a structured learning path for me. It actually understood my level and guided me step by step.”",
    name: "Priya",
    role: "Data Scientist",
    avatar: "/Image/Hero/testimonials-profile.jpg",
  },
  {
    rating: 5,
    text:
      "“nxNearby completely changed the way I learn. Instead of jumping randomly between YouTube, courses and blogs, the platform built a structured learning path for me. It actually understood my level and guided me step by step.”",
    name: "Sarah",
    role: "Software Engineer",
    avatar: "/Image/Hero/testimonials-profile.jpg",
  },
  {
    rating: 5,
    text:
      "“nxNearby completely changed the way I learn. Instead of jumping randomly between YouTube, courses and blogs, the platform built a structured learning path for me. It actually understood my level and guided me step by step.”",
    name: "Alex",
    role: "Product Designer",
    avatar: "/Image/Hero/testimonials-profile.jpg",
  },
  {
    rating: 5,
    text:
      "“nxNearby completely changed the way I learn. Instead of jumping randomly between YouTube, courses and blogs, the platform built a structured learning path for me. It actually understood my level and guided me step by step.”",
    name: "Priya",
    role: "Data Scientist",
    avatar: "/Image/Hero/testimonials-profile.jpg",
  },
];

export default function TestimonialsSection() {
  const { t } = useTranslation();
  const viewportRef = useRef(null);
  const firstCardRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [cardsVisible, setCardsVisible] = useState(3);

  const CARD_W = 500;
  const GAP = 28;
  const computeCardsVisible = (viewportWidth) => {
    if (!viewportWidth) return 1;
    const perCard = CARD_W + GAP;
    const fit = Math.floor((viewportWidth + GAP) / perCard);
    return Math.min(3, Math.max(1, fit));
  };

  // load testimonials from translation (expects an array of objects). fallback to default if not found
  let translatedItems = t("home.testimonials.items", { returnObjects: true });
  const testimonials = Array.isArray(translatedItems) ? translatedItems : DEFAULT_TESTIMONIALS;

  // scroll to leftmost index
  const scrollToIndex = (newIndex) => {
    const vp = viewportRef.current;
    if (!vp) return;
    const scrollAmount = newIndex * (CARD_W + GAP);
    vp.scrollTo({ left: scrollAmount, behavior: "smooth" });
    setIndex(newIndex);
  };

  const onNext = () => {
    const maxIndex = Math.max(0, testimonials.length - cardsVisible);
    const nextIndex = Math.min(index + 1, maxIndex);
    if (nextIndex !== index) scrollToIndex(nextIndex);
  };

  const onPrev = () => {
    const prevIndex = Math.max(0, index - 1);
    if (prevIndex !== index) scrollToIndex(prevIndex);
  };

  // Observe viewport size and recompute visible cards
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        const visible = computeCardsVisible(width);
        setCardsVisible((prev) => {
          if (prev === visible) return prev;
          setIndex((currIdx) => {
            const maxIdx = Math.max(0, testimonials.length - visible);
            return Math.min(currIdx, maxIdx);
          });
          return visible;
        });
      }
    });

    ro.observe(vp);

    const initWidth = vp.getBoundingClientRect().width;
    setCardsVisible(computeCardsVisible(initWidth));

    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const maxIndex = Math.max(0, testimonials.length - cardsVisible);
    if (index > maxIndex) {
      scrollToIndex(maxIndex);
    } else {
      scrollToIndex(index);
    }
  }, [cardsVisible]);

  const maxIndex = Math.max(0, testimonials.length - cardsVisible);

  const ariaLabel = t("home.testimonials.ariaLabel", "Loved by thousands of professionals");
  const headerTitlePart = t("home.testimonials.header.titlePart1", "Loved by");
  const headerAccent = t("home.testimonials.header.accent", "Thousands of\nProfessionals");
  const headerSub = t(
    "home.testimonials.header.sub",
    "See how nxNearby has helped professionals like you accelerate their careers and achieve their goals."
  );

  return (
    <section className="testimonials-section" role="region" aria-label={ariaLabel}>
      <div className="testimonials-inner" style={{ zIndex: 2 }}>
        <header className="testimonials-header">
          <h2 className="testimonials-title">
            {headerTitlePart}{" "}
            <span className="accent">
              {headerAccent.split("\n").map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  {i < headerAccent.split("\n").length - 1 && <br />}
                </React.Fragment>
              ))}
            </span>
          </h2>
          <p className="testimonials-sub">{headerSub}</p>
        </header>

        <div className="testimonials-frame" aria-hidden={false}>
          <button
            className="nav-chev left"
            aria-label={t("home.testimonials.prevAria", "Previous testimonials")}
            onClick={onPrev}
            disabled={index <= 0}
          >
            <img src="/Image/Hero/Arrow.svg" width="19px" height="20px" alt="" />
          </button>

          <div className="testimonials-viewport" ref={viewportRef} aria-live="polite">
            <div className="testimonials-row" role="list" aria-label={t("home.testimonials.listAria", "Testimonials list")}>
              {testimonials.map((tItem, i) => (
                <article
                  className="testimonial-card"
                  key={i}
                  role="listitem"
                  ref={i === 0 ? firstCardRef : null}
                >
                  <div className="rating" aria-hidden>
                    {Array.from({ length: tItem.rating || 5 }).map((_, idx) => (
                      <span key={idx}>
                        <img src="/Image/Hero/Star.svg" alt="★" width="22px" height="22px" />
                      </span>
                    ))}
                  </div>

                  <blockquote className="testimonial-text">{tItem.text}</blockquote>

                  <hr className="card-sep" />

                  <div className="card-meta">
                    <img className="avatar-testimonials" src={tItem.avatar} alt={`${tItem.name} avatar`} />
                    <div className="meta-text">
                      <div className="meta-name">{tItem.name}</div>
                      <div className="meta-role">{tItem.role}</div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>

          <button
            className="nav-chev right"
            aria-label={t("home.testimonials.nextAria", "Next testimonials")}
            onClick={onNext}
            disabled={index >= maxIndex}
          >
            <img src="/Image/Hero/Right arrow.svg" width="19px" height="20px" alt="" />
          </button>
        </div>
      </div>
    </section>
  );
}
